import { motion } from "framer-motion";
import { MessageCircle, Phone, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToHash } from "@/components/Navbar";
import {
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_HREF,
  INSTAGRAM_URL,
  OFFICE_ADDRESS,
  PROJECT_LOCATION,
  whatsappHref,
} from "@/lib/contact";

const WA_SUNIL = whatsappHref(
  "Hi Ashish, I'm contacting the Homewiz.in independent advisory desk about Fairfox EON investment opportunities. Please share current options."
);

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-white/[0.07] bg-[#0B0C0E]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">Independent Marketing &amp; Investment Advisory</p>
            <img
              src="/homewiz-logo.png"
              alt="Homewiz.in — Premium Real Estate"
              data-testid="footer-homewiz-logo"
              className="mt-4 h-28 sm:h-32 w-auto object-contain -ml-3"
            />
            <p data-testid="footer-office-address" className="mt-4 max-w-md text-sm text-[#9CA3AF] leading-relaxed">
              <span className="text-[#D1D5DB]">{OFFICE_ADDRESS.label}</span><br />
              {OFFICE_ADDRESS.line1}<br />
              {OFFICE_ADDRESS.line2}<br />
              {OFFICE_ADDRESS.line3}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href={WA_SUNIL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-btn"
                className="px-6 py-3 bg-[#D4AF37] text-[#0B0C0E] font-sans text-xs font-semibold uppercase tracking-[0.15em] rounded-full hover:bg-[#F3E5AB] hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-all duration-300 flex items-center gap-2"
              >
                <MessageCircle size={14} />
                WhatsApp Ashish
              </a>
              <a
                href={CONTACT_PHONE_HREF}
                data-testid="footer-call-btn"
                className="px-6 py-3 border border-[#D4AF37]/40 text-[#F3F4F6] font-mono text-xs tracking-[0.1em] rounded-full hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-300 flex items-center gap-2"
              >
                <Phone size={14} className="text-[#D4AF37]" />
                {CONTACT_PHONE_DISPLAY}
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-btn"
                aria-label="Homewiz.in on Instagram"
                className="w-[46px] h-[46px] rounded-full border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0B0C0E] hover:shadow-[0_0_24px_rgba(212,175,55,0.35)] transition-all duration-300"
              >
                <Instagram size={16} />
              </a>
            </div>
          </div>
          <div className="flex flex-col justify-between gap-8">
            <nav className="flex flex-wrap gap-x-8 gap-y-3">
              {[["EON", "#top"], ["Why EON", "#why-eon"], ["Primary / Resale", "#primary-resale"], ["Opportunities", "#opportunities"], ["Investment", "#calculator"], ["Location", "#location"], ["Site Visit", "#visit"]].map(([label, hash]) => (
                <a
                  key={hash + label}
                  href={hash}
                  data-testid={`footer-link-${label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                  onClick={(e) => { e.preventDefault(); scrollToHash(hash); }}
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF] hover:text-[#D4AF37] transition-colors duration-300"
                >
                  {label}
                </a>
              ))}
            </nav>
            <p data-testid="footer-disclaimer" className="text-[11px] leading-relaxed text-[#6B7280] max-w-lg">
              EONFAIRFOX.IN is an independent marketing and investment advisory website operated by Homewiz.in. It
              is not the official website of Fairfox IT Infra Pvt. Ltd. Project information, pricing, availability
              and leasing information are subject to verification.
            </p>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/[0.05] flex flex-col sm:flex-row justify-between gap-3 text-[11px] text-[#4B5563]">
          <span>© {new Date().getFullYear()} Homewiz.in — EON Fairfox Investment Desk. All rights reserved.</span>
          <span data-testid="footer-project-location" className="font-mono tracking-[0.15em]">Project: {PROJECT_LOCATION} · RERA: UPRERAPRJ211016 · UPRERAPRJ716606 · UPRERAPRJ882013</span>
          <span className="flex items-center gap-5">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-[#4B5563] hover:text-[#D4AF37] transition-colors duration-300">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="footer-terms-link" className="text-[#4B5563] hover:text-[#D4AF37] transition-colors duration-300">
              Terms of Use
            </Link>
            <button
              data-testid="footer-cookie-settings-btn"
              onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
              className="text-[#4B5563] hover:text-[#D4AF37] transition-colors duration-300"
            >
              Cookie Settings
            </button>
            <Link to="/admin" data-testid="footer-admin-link" className="text-[#3a3f4a] hover:text-[#9CA3AF] transition-colors duration-300">
              Desk Login
            </Link>
          </span>
        </div>
      </div>
    </footer>
  );
}
